import { GuessResult } from '@/types/game';

/**
 * Calculate score based on the accuracy of the guess
 * Perfect guess = 1000 points
 * Points decrease with percentage error
 */
export function calculateScore(guessedPrice: number, actualPrice: number): GuessResult {
  const difference = Math.abs(guessedPrice - actualPrice);
  const percentageError = (difference / actualPrice) * 100;

  let pointsEarned = 0;

  // Scoring system based on percentage error
  if (percentageError === 0) {
    pointsEarned = 1000; // Perfect guess
  } else if (percentageError <= 5) {
    pointsEarned = 900;
  } else if (percentageError <= 10) {
    pointsEarned = 800;
  } else if (percentageError <= 15) {
    pointsEarned = 700;
  } else if (percentageError <= 20) {
    pointsEarned = 600;
  } else if (percentageError <= 30) {
    pointsEarned = 400;
  } else if (percentageError <= 40) {
    pointsEarned = 200;
  } else if (percentageError <= 50) {
    pointsEarned = 100;
  } else {
    pointsEarned = 0;
  }

  return {
    guessedPrice,
    actualPrice,
    difference,
    percentageError,
    pointsEarned,
  };
}

/**
 * Get feedback message based on percentage error
 */
export function getFeedbackMessage(percentageError: number): string {
  if (percentageError === 0) return "Perfect! Exact match!";
  if (percentageError <= 5) return "Incredible! Almost perfect!";
  if (percentageError <= 10) return "Excellent guess!";
  if (percentageError <= 15) return "Great job!";
  if (percentageError <= 20) return "Good guess!";
  if (percentageError <= 30) return "Not bad!";
  if (percentageError <= 40) return "Could be better...";
  if (percentageError <= 50) return "Keep practicing!";
  return "Way off...";
}

export type Tier = 'great' | 'ok' | 'bad';

/** Accuracy bucket used for colour-coding results */
export function getTier(percentageError: number): Tier {
  if (percentageError <= 10) return 'great';
  if (percentageError <= 30) return 'ok';
  return 'bad';
}

export const TIER_BG: Record<Tier, string> = {
  great: 'bg-good',
  ok: 'bg-warn',
  bad: 'bg-bad',
};

export const TIER_TEXT: Record<Tier, string> = {
  great: 'text-good',
  ok: 'text-warn',
  bad: 'text-bad',
};

export const TIER_EMOJI: Record<Tier, string> = {
  great: '🟩',
  ok: '🟨',
  bad: '🟥',
};

/** Translation key for the feedback headline */
export function getFeedbackKey(percentageError: number) {
  if (percentageError === 0) return 'perfect';
  if (percentageError <= 5) return 'excellent';
  if (percentageError <= 15) return 'veryGood';
  if (percentageError <= 20) return 'good';
  if (percentageError <= 30) return 'notBad';
  if (percentageError <= 50) return 'couldBeBetter';
  return 'farOff';
}

/** Translation key for the final rank, from score ratio (0..1) */
export function getRankKey(ratio: number) {
  if (ratio >= 0.8) return 'rank1';
  if (ratio >= 0.6) return 'rank2';
  if (ratio >= 0.4) return 'rank3';
  if (ratio >= 0.2) return 'rank4';
  return 'rank5';
}

/**
 * Format price for display
 */
export function formatPrice(price: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
}
